import { CheckCircleOutline, Error } from '@mui/icons-material'
import {
    Box,
    Button,
    Stack,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TablePagination,
    TableRow,
    Typography,
} from '@mui/material'
import dayjs from 'dayjs'
import { useEffect, useState } from 'react'
import { Patient } from '../services/models/member'
import { getPatients } from '../services/patientService'
import { DataPanel } from './DataPanel'

interface ITableEntry extends Patient {
    payerName: string
    effectiveStartDate: number
    effectiveEndDate: number
}
const formatDate = (timestamp: number) => dayjs(timestamp).format('MM/DD/YYYY')

const PatientTable = () => {
    const [tableData, setTableData] = useState<ITableEntry[]>([])
    const [showInactiveData, setShowInactiveData] = useState(false)
    const [recordCount, setRecordCount] = useState(0)
    const [page, setPage] = useState(0)
    const [rowsPerPage, setRowsPerPage] = useState(5)
    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage)
    }

    const handleChangeRowsPerPage = (
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => {
        setRowsPerPage(+event.target.value)
        setPage(0)
    }
    useEffect(() => {
        const fetchPatients = async () => {
            const {
                data: { data, numberOfEntries },
            } = await getPatients(page * rowsPerPage, rowsPerPage, showInactiveData)
            setTableData(data)
            setRecordCount(numberOfEntries)
        }
        fetchPatients()
    }, [page, rowsPerPage, showInactiveData])

    const renderStatus = (active: boolean) => {
        if (active) {
            return <CheckCircleOutline color="primary" fontSize="large" sx={{ strokeWidth: 0 }} />
        }
        return <Error color="warning" fontSize="large" sx={{ strokeWidth: 0 }} />
    }

    return (
        <>
            <DataPanel
                content={
                    <>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                margin: 2,
                                flexGrow: 1,
                            }}
                        >
                            <Box
                                sx={{
                                    flexGrow: 1,
                                }}
                            >
                                <Stack
                                    direction="row"
                                    spacing={1}
                                    sx={{ alignItems: 'center', display: 'flex' }}
                                >
                                    <Typography fontSize="medium" fontWeight="bold">
                                        Show Inactive
                                    </Typography>
                                    <Switch
                                        checked={showInactiveData}
                                        // as of now we are refetching data when we do this...
                                        onChange={() => setShowInactiveData(!showInactiveData)}
                                    />
                                </Stack>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <Button
                                    sx={{ margin: 2 }}
                                    onClick={() => {
                                        setPage(0)
                                    }}
                                >
                                    Reload
                                </Button>
                                <Button onClick={() => {}} disabled>
                                    Add New
                                </Button>
                            </Box>
                        </Box>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Status</TableCell>
                                    <TableCell>Payer</TableCell>
                                    <TableCell>Name</TableCell>
                                    <TableCell>Member Account #</TableCell>
                                    <TableCell>Effective Start</TableCell>
                                    <TableCell>Effective End</TableCell>
                                    <TableCell>Last Updated On</TableCell>
                                    <TableCell>Relationship</TableCell>
                                    <TableCell>Action</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {tableData
                                    .filter((entry) =>
                                        // redo aggregation to support this? Or only fetch when flipped?
                                        showInactiveData ? true : entry?.activeAccount,
                                    )
                                    .map((entry) => (
                                        <TableRow key={entry.id}>
                                            <TableCell
                                                sx={{
                                                    textAlign: 'center',
                                                    verticalAlign: 'bottom',
                                                }}
                                            >
                                                {renderStatus(entry.activeAccount)}
                                            </TableCell>
                                            <TableCell>{entry.payerName}</TableCell>
                                            <TableCell>{`${entry.lastName}, ${entry.firstName}`}</TableCell>
                                            <TableCell>{entry.accountNumber}</TableCell>
                                            <TableCell>
                                                {formatDate(entry.effectiveStartDate)}
                                            </TableCell>
                                            <TableCell>
                                                {formatDate(entry.effectiveEndDate)}
                                            </TableCell>
                                            <TableCell>{formatDate(entry.createdAt)}</TableCell>
                                            <TableCell>{entry.relationshipToSubscriber}</TableCell>
                                            <TableCell>
                                                <Button disabled onClick={() => {}}>
                                                    Details
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                            </TableBody>
                        </Table>
                        <>
                            <Box
                                sx={{
                                    paddingTop: 1,
                                    marginLeft: 2,
                                    marginRight: 2,
                                    alignItems: 'center',
                                    display: 'flex',
                                }}
                            >
                                <Box sx={{ flexGrow: 1 }}></Box>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <TablePagination
                                        rowsPerPageOptions={[5, 10, 15, 20, 50, 100]}
                                        component="div"
                                        count={recordCount}
                                        page={page}
                                        onPageChange={handleChangePage}
                                        rowsPerPage={rowsPerPage}
                                        onRowsPerPageChange={handleChangeRowsPerPage}
                                    />
                                </Box>
                            </Box>
                        </>
                    </>
                }
            />
        </>
    )
}

export default PatientTable
